import { useState } from 'react';
import { Switch } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';

import { Button, Input, Textarea } from '../../../../components/ui';
import { useT } from '../../../../i18n';
import { soVN } from '../../../../lib/dinh-dang';
import { KICH_BAN_RONG, soThuThap } from '../../nghiep-vu/cau-hinh-bot';
import type { Scenario } from '../../types';
import { KichBanDialog } from './KichBan';
import { Khoi, type TabProps } from './chung';

export function TabHanhVi({ f, dat, suaDuoc }: TabProps) {
  const { t, locale } = useT();
  // `null` = đóng · `'moi'` = thêm mới · số = chỉ mục đang sửa. Một biến mang cả ba trạng thái thay vì một cờ cạnh một
  // chỉ mục — cờ và chỉ mục là hai thứ phải nhớ cập nhật cùng nhau.
  const [suaKb, setSuaKb] = useState<number | 'moi' | null>(null);
  const so = (n: number) => soVN(n, locale);

  return (
    <div className="space-y-4">
      <Khoi ten={t('troLyAiBot.mucHuongDan')} mo={t('troLyAiBot.mucHuongDanMo')}>
        <Textarea
          autoSize={{ minRows: 4, maxRows: 12 }}
          disabled={!suaDuoc}
          aria-label={t('troLyAiBot.mucHuongDan')}
          value={f.systemPrompt}
          onChange={(e) => dat('systemPrompt', e.target.value)}
          placeholder={t('troLyAiBotViDu.botHuongDan')}
        />
      </Khoi>

      <Khoi ten={t('troLyAiBot.mucFaq')} mo={t('troLyAiBot.mucFaqMo')}>
        <div className="space-y-2">
          {f.faqs.map((q, i) => (
            <div key={i} className="flex gap-2">
              <div className="min-w-0 flex-1 space-y-1.5">
                <Input
                  disabled={!suaDuoc}
                  aria-label={t('troLyAiBot.cauHoiSo', { i: so(i + 1) })}
                  value={q.question}
                  onChange={(e) => dat('faqs', f.faqs.map((x, j) => (j === i ? { ...x, question: e.target.value } : x)))}
                  placeholder={t('troLyAiBot.cauHoi')}
                />
                <Textarea
                  autoSize={{ minRows: 2, maxRows: 8 }}
                  disabled={!suaDuoc}
                  aria-label={t('troLyAiBot.cauTraLoiSo', { i: so(i + 1) })}
                  value={q.answer}
                  onChange={(e) => dat('faqs', f.faqs.map((x, j) => (j === i ? { ...x, answer: e.target.value } : x)))}
                  placeholder={t('troLyAiBot.cauTraLoi')}
                />
              </div>
              {suaDuoc ? (
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={t('troLyAiBot.xoaCauHoi', { i: so(i + 1) })}
                  icon={<DeleteOutlined />}
                  onClick={() => dat('faqs', f.faqs.filter((_, j) => j !== i))}
                />
              ) : null}
            </div>
          ))}
          {suaDuoc ? (
            <Button
              size="sm"
              variant="secondary"
              icon={<PlusOutlined />}
              onClick={() => dat('faqs', [...f.faqs, { question: '', answer: '' }])}
            >
              {t('troLyAiBot.themCauHoi')}
            </Button>
          ) : null}
        </div>
      </Khoi>

      <Khoi ten={t('troLyAiBot.mucKichBan')} mo={t('troLyAiBot.mucKichBanMo')}>
        <label className="flex items-center gap-2 text-xs">
          <Switch
            size="small"
            checked={f.scenariosEnabled}
            disabled={!suaDuoc}
            onChange={(v) => dat('scenariosEnabled', v)}
          />
          {t('troLyAiBot.batKichBan')}
        </label>

        {f.scenariosEnabled ? (
          <div className="space-y-2">
            {/*
              Thẻ TÓM TẮT, không phải ô nhập mở sẵn: một kịch bản có tới năm phần, mở hết thì ba kịch bản đã chiếm cả màn.
              Ba con số nói kịch bản đã khai tới đâu — thiếu chúng thì kịch bản chỉ có tên trông y hệt kịch bản khai đủ.
            */}
            {f.scenarios.map((k, i) => (
              <div key={i} className="flex items-start gap-2 rounded border border-default p-2.5">
                <button
                  type="button"
                  disabled={!suaDuoc}
                  onClick={() => setSuaKb(i)}
                  aria-label={t('troLyAiBot.suaKichBan', { ten: k.name || so(i + 1) })}
                  className="min-w-0 flex-1 text-start"
                >
                  <p className="truncate text-xs font-medium text-content-primary">
                    {k.name || <span className="text-content-muted">{t('troLyAiBot.chuaDatTen')}</span>}
                  </p>
                  {k.trigger ? <p className="truncate text-xs text-content-secondary">{k.trigger}</p> : null}
                  <p className="mt-0.5 flex flex-wrap gap-x-2 text-xs text-content-muted">
                    <span>{t('troLyAiBot.kbThongTin', { n: so(soThuThap(k)) })}</span>
                    <span>· {t('troLyAiBot.kbBuoc', { n: so(k.steps?.length ?? 0) })}</span>
                    <span>· {t('troLyAiBot.kbQuyTac', { n: so(k.rules?.length ?? 0) })}</span>
                    {!k.steps?.length && !k.reply ? (
                      <span className="text-warning-700">· {t('troLyAiBot.kbChuaCoBuoc')}</span>
                    ) : null}
                  </p>
                </button>
                {suaDuoc ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={t('troLyAiBot.xoaKichBan', { ten: k.name || so(i + 1) })}
                    icon={<DeleteOutlined />}
                    onClick={() => dat('scenarios', f.scenarios.filter((_, j) => j !== i))}
                  />
                ) : null}
              </div>
            ))}
            {suaDuoc ? (
              <Button size="sm" variant="secondary" icon={<PlusOutlined />} onClick={() => setSuaKb('moi')}>
                {t('troLyAiBot.themKichBan')}
              </Button>
            ) : null}

            {suaKb !== null ? (
              <KichBanDialog
                // `key` ép dựng lại khi đổi kịch bản đang sửa: hộp giữ bản nháp trong state riêng.
                key={String(suaKb)}
                open
                disabled={!suaDuoc}
                banDau={suaKb === 'moi' ? null : { ...KICH_BAN_RONG, ...(f.scenarios[suaKb] as Partial<Scenario>) }}
                onClose={() => setSuaKb(null)}
                onLuu={(k) => {
                  dat('scenarios', suaKb === 'moi' ? [...f.scenarios, k] : f.scenarios.map((x, j) => (j === suaKb ? k : x)));
                  setSuaKb(null);
                }}
              />
            ) : null}
          </div>
        ) : null}
      </Khoi>
    </div>
  );
}
